// Settings → General: the admin's switch for table reservations (F-RSV-N1)
// and online booking on the storefront (F-RSV-N2). Off until an admin turns
// it on; the server validates every field and refuses a non-admin write, so
// the card is admin-only as well.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'
import {
  RESERVATIONS_SETTING_KEY,
  reservationsConfigFrom,
  type HoursWindow,
  type ReservationsConfig,
} from '@/lib/reservations'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function defaultCustomHours(): HoursWindow[] {
  return DAYS.map(() => ({ closed: false, open: '12:00', close: '23:00' }))
}

function NumberField({
  id,
  label,
  hint,
  value,
  onChange,
  min,
  max,
}: {
  id: string
  label: string
  hint?: string
  value: number
  onChange: (n: number) => void
  min: number
  max: number
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
      />
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

export function ReservationsSettingCard() {
  const all = useAllSettings()
  const stored = all?.[RESERVATIONS_SETTING_KEY]
  const qc = useQueryClient()
  const { toast } = useToast()
  const [value, setValue] = useState<ReservationsConfig>(() => reservationsConfigFrom(stored))
  useEffect(() => setValue(reservationsConfigFrom(stored)), [stored])

  const save = useMutation({
    mutationFn: async (next: ReservationsConfig) => {
      const res = await apiClient.updateSetting(RESERVATIONS_SETTING_KEY, next)
      if (!res.success) throw new Error(res.message || 'Could not save')
      return res
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      qc.invalidateQueries({ queryKey: ['reservations'] })
      toast({ title: 'Settings saved', description: 'Reservations updated.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  if (getStoredUserRole() !== 'admin') return null

  const dirty = JSON.stringify(value) !== JSON.stringify(reservationsConfigFrom(stored))
  const setOnline = (patch: Partial<ReservationsConfig['online']>) =>
    setValue((p) => ({ ...p, online: { ...p.online, ...patch } }))
  const hours = value.online.hours ?? defaultCustomHours()
  const setDay = (i: number, patch: Partial<HoursWindow>) =>
    setOnline({ hours: hours.map((d, j) => (j === i ? { ...d, ...patch } : d)) })

  const submit = () => {
    const next: ReservationsConfig = {
      ...value,
      online: {
        ...value.online,
        ...(value.online.hours_source === 'custom' ? { hours } : {}),
      },
    }
    if (next.online.hours_source !== 'custom') delete next.online.hours
    save.mutate(next)
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Table reservations</CardTitle>
        <CardDescription>
          Take bookings by phone, WhatsApp or online, hold tables on the floor plan, and keep a walk-in waitlist.
          Bookings are managed on the Reservations page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start gap-3">
          <Switch
            id="reservations-enabled"
            checked={value.enabled}
            onCheckedChange={(v) => setValue((p) => ({ ...p, enabled: v }))}
          />
          <div className="space-y-0.5">
            <Label htmlFor="reservations-enabled" className="text-sm font-medium">
              Take table reservations
            </Label>
            <p className="text-sm text-muted-foreground">
              Turning this off hides every reservation screen. Existing bookings can still be looked up and cancelled.
            </p>
          </div>
        </div>

        {value.enabled && (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <NumberField
                id="resv-duration"
                label="Booking length (minutes)"
                value={value.default_duration_minutes}
                min={15}
                max={720}
                onChange={(n) => setValue((p) => ({ ...p, default_duration_minutes: n }))}
              />
              <NumberField
                id="resv-hold"
                label="Hold table before (minutes)"
                hint="The floor shows the table as reserved this long before the booking."
                value={value.hold_minutes}
                min={0}
                max={240}
                onChange={(n) => setValue((p) => ({ ...p, hold_minutes: n }))}
              />
              <NumberField
                id="resv-grace"
                label="No-show after (minutes)"
                hint="How late a guest can be before staff may mark a no-show."
                value={value.no_show_grace_minutes}
                min={0}
                max={240}
                onChange={(n) => setValue((p) => ({ ...p, no_show_grace_minutes: n }))}
              />
            </div>

            <div className="space-y-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <Switch
                  id="reservations-online"
                  checked={value.online.enabled}
                  onCheckedChange={(v) => setOnline({ enabled: v })}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="reservations-online" className="text-sm font-medium">
                    Online booking on your website
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Adds &ldquo;Book a table&rdquo; to your online store. Guests verify their email, like they do for
                    orders.
                  </p>
                </div>
              </div>

              {value.online.enabled && (
                <>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label>New online bookings</Label>
                      <Select
                        value={value.online.confirmation}
                        onValueChange={(v) => setOnline({ confirmation: v === 'auto' ? 'auto' : 'manual' })}
                      >
                        <SelectTrigger aria-label="New online bookings">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Staff approve each one</SelectItem>
                          <SelectItem value="auto">Confirm automatically</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Time slots every</Label>
                      <Select
                        value={String(value.online.slot_interval_minutes)}
                        onValueChange={(v) => setOnline({ slot_interval_minutes: Number(v) })}
                      >
                        <SelectTrigger aria-label="Time slots every">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                          <SelectItem value="60">60 minutes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <NumberField
                      id="resv-days"
                      label="Book up to (days ahead)"
                      value={value.online.days_ahead}
                      min={1}
                      max={180}
                      onChange={(n) => setOnline({ days_ahead: n })}
                    />
                    <NumberField
                      id="resv-notice"
                      label="Minimum notice (minutes)"
                      value={value.online.min_notice_minutes}
                      min={0}
                      max={10080}
                      onChange={(n) => setOnline({ min_notice_minutes: n })}
                    />
                    <NumberField
                      id="resv-maxparty"
                      label="Largest party online"
                      hint="Bigger groups are asked to call."
                      value={value.online.max_party_size}
                      min={1}
                      max={50}
                      onChange={(n) => setOnline({ max_party_size: n })}
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="resv-combine"
                      checked={value.online.allow_combining_tables}
                      onCheckedChange={(v) => setOnline({ allow_combining_tables: v === true })}
                    />
                    <div className="space-y-0.5">
                      <Label htmlFor="resv-combine" className="text-sm font-medium">
                        Allow pushing tables together
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        When no single table fits the party, offer the time if enough free seats exist across tables.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-1.5 sm:max-w-xs">
                      <Label>Booking hours</Label>
                      <Select
                        value={value.online.hours_source}
                        onValueChange={(v) =>
                          setOnline(
                            v === 'custom'
                              ? { hours_source: 'custom', hours: value.online.hours ?? defaultCustomHours() }
                              : { hours_source: 'storefront' },
                          )
                        }
                      >
                        <SelectTrigger aria-label="Booking hours">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="storefront">Same as online store hours</SelectItem>
                          <SelectItem value="custom">Set booking hours</SelectItem>
                        </SelectContent>
                      </Select>
                      {value.online.hours_source === 'storefront' && (
                        <p className="text-xs text-muted-foreground">
                          Uses the opening hours set on the Website page. With none set, no times are offered online.
                        </p>
                      )}
                    </div>
                    {value.online.hours_source === 'custom' && (
                      <div className="space-y-2">
                        {DAYS.map((day, i) => (
                          <div key={day} className="flex flex-wrap items-center gap-3 text-sm">
                            <span className="w-24 font-medium">{day}</span>
                            <label className="flex items-center gap-2 text-muted-foreground">
                              <Checkbox
                                checked={hours[i]?.closed === true}
                                onCheckedChange={(v) => setDay(i, { closed: v === true })}
                                aria-label={`${day} closed`}
                              />
                              Closed
                            </label>
                            {!hours[i]?.closed && (
                              <>
                                <Input
                                  type="time"
                                  className="w-32"
                                  aria-label={`${day} opens`}
                                  value={hours[i]?.open ?? ''}
                                  onChange={(e) => setDay(i, { open: e.target.value })}
                                />
                                <span className="text-muted-foreground">to</span>
                                <Input
                                  type="time"
                                  className="w-32"
                                  aria-label={`${day} closes`}
                                  value={hours[i]?.close ?? ''}
                                  onChange={(e) => setDay(i, { close: e.target.value })}
                                />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        <Button disabled={save.isPending || !dirty} onClick={submit}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
